import React, { useState } from "react";
import {
  Box, Typography, Paper, TextField, Button,
  Grid, Divider, Switch, FormControlLabel, CircularProgress
} from "@mui/material";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useLista } from "../hooks/useListe";
import SelectConAggiunta from "../components/SelectConAggiunta";

const areeTecniche = [
  { id: "audio", label: "Acquisizione e distribuzione audio in sala" },
  { id: "video", label: "Riprese video" },
  { id: "connessione", label: "Connessione" },
  { id: "regia", label: "Regia" },
  { id: "contributi", label: "Gestione contributi in sala" },
  { id: "trasmissione", label: "Trasmissione a pubblico remoto" },
  { id: "relatori", label: "Presenza relatori remoti" },
  { id: "noleggioMateriale", label: "Noleggio materiale" },
];

const serviziAggiuntivi = [
  {
    id: "glamotion",
    label: "Glamotion",
    logo: "/glamotion-logo-norm.png",
    noteLabel: "Note Glamotion",
  },
  {
    id: "multiLanguage",
    label: "MultiLanguage",
    logo: "/multilanguage-logo-norm.png",
    noteLabel: "Note MultiLanguage",
  },
  {
    id: "votazioniLumi",
    label: "Votazioni con LUMI",
    logo: "/lumi-logo-norm.png",
    noteLabel: "Note Votazioni LUMI",
  },
];

export default function NuovoEvento() {
  const navigate = useNavigate();
  const { items: clienti, loading: loadingClienti, aggiungiItem: aggiungiCliente } = useLista("clienti");

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    titolo: "",
    clienteId: "",
    data: "",
    location: "",
    budget: "",
    opportunitaCRM: "",
    contattiReferenti: "",
    aree: Object.fromEntries(areeTecniche.map((a) => [a.id, { attiva: false, note: "" }])),
    servizi: Object.fromEntries(serviziAggiuntivi.map((s) => [s.id, { attivo: false, note: "" }])),
  });

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleArea(areaId, field, value) {
    setForm((prev) => ({
      ...prev,
      aree: {
        ...prev.aree,
        [areaId]: { ...prev.aree[areaId], [field]: value },
      },
    }));
  }

  function handleServizio(servizioId, field, value) {
    setForm((prev) => ({
      ...prev,
      servizi: {
        ...prev.servizi,
        [servizioId]: { ...prev.servizi[servizioId], [field]: value },
      },
    }));
  }

  async function handleSalva() {
    if (!form.clienteId || !form.data) return;
    setSaving(true);
    try {
      await addDoc(collection(db, "eventi"), {
        ...form,
        stato: "qualificazione",
        createdAt: serverTimestamp(),
      });
      navigate("/");
    } catch (error) {
      console.error("Errore salvataggio:", error);
    }
    setSaving(false);
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Nuovo Preventivo
      </Typography>

      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={2} color="primary">
          Informazioni base
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Titolo evento" value={form.titolo}
              onChange={(e) => handleChange("titolo", e.target.value)} required />
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectConAggiunta label="Cliente" value={form.clienteId}
              onChange={(v) => handleChange("clienteId", v)}
              items={clienti} loading={loadingClienti}
              onAggiungi={aggiungiCliente} required />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Data evento" type="date" value={form.data}
              onChange={(e) => handleChange("data", e.target.value)}
              InputLabelProps={{ shrink: true }} required />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Location" value={form.location}
              onChange={(e) => handleChange("location", e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Budget indicativo (€)" type="number" value={form.budget}
              onChange={(e) => handleChange("budget", e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Opportunità SuiteCRM (link o codice)" value={form.opportunitaCRM}
              onChange={(e) => handleChange("opportunitaCRM", e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={3}
              label="Nominativi e contatti di riferimento"
              placeholder="Inserisci i contatti dei referenti del progetto"
              value={form.contattiReferenti}
              onChange={(e) => handleChange("contattiReferenti", e.target.value)} />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={2} color="primary">
          Requisiti tecnici
        </Typography>

        {/* Aree tecniche */}
        {areeTecniche.map((area) => (
          <Box key={area.id} mb={2}>
            <FormControlLabel
              control={
                <Switch checked={form.aree[area.id].attiva}
                  onChange={(e) => handleArea(area.id, "attiva", e.target.checked)}
                  color="primary" />
              }
              label={<Typography fontWeight="medium">{area.label}</Typography>}
            />
            {form.aree[area.id].attiva && (
              <TextField fullWidth multiline rows={2} label="Note e dettagli"
                value={form.aree[area.id].note}
                onChange={(e) => handleArea(area.id, "note", e.target.value)}
                sx={{ mt: 1, ml: 4 }} />
            )}
            <Divider sx={{ mt: 2 }} />
          </Box>
        ))}

        <Box sx={{ mt: 1 }} />

        {/* Servizi aggiuntivi */}
        {serviziAggiuntivi.map((servizio) => (
          <Box key={servizio.id} mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box sx={{
                width: 120, height: 36,
                display: "flex", alignItems: "center", justifyContent: "flex-start",
                flexShrink: 0,
              }}>
                <img
                  src={servizio.logo}
                  alt={servizio.label}
                  style={{ maxHeight: 36, maxWidth: 120, objectFit: "contain" }}
                />
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.servizi[servizio.id].attivo}
                    onChange={(e) => handleServizio(servizio.id, "attivo", e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography fontWeight="medium">{servizio.label}</Typography>
                }
              />
            </Box>
            {form.servizi[servizio.id].attivo && (
              <TextField fullWidth multiline rows={2}
                label={servizio.noteLabel}
                value={form.servizi[servizio.id].note}
                onChange={(e) => handleServizio(servizio.id, "note", e.target.value)}
                sx={{ mt: 1, ml: 16 }} />
            )}
            <Divider sx={{ mt: 2 }} />
          </Box>
        ))}
      </Paper>

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button variant="outlined" onClick={() => navigate("/")}>Annulla</Button>
        <Button variant="contained" onClick={handleSalva}
          disabled={saving || !form.clienteId || !form.data}>
          {saving ? <CircularProgress size={20} /> : "Salva preventivo"}
        </Button>
      </Box>
    </Box>
  );
}